import type { ApiPaths } from '@per-ardua/fandom-core';

export const API_URL = (import.meta.env.VITE_ADMIN_API_URL as string).replace(/\/+$/, '');

type Json<T> = T extends { content: { 'application/json': infer B } } ? B : never;
type Op<P extends keyof ApiPaths, M extends keyof ApiPaths[P]> = ApiPaths[P][M];
type Res<P extends keyof ApiPaths, M extends keyof ApiPaths[P], S extends PropertyKey> =
  Op<P, M> extends { responses: infer R } ? (S extends keyof R ? Json<R[S]> : never) : never;
type Req<P extends keyof ApiPaths, M extends keyof ApiPaths[P]> =
  Op<P, M> extends { requestBody: infer B } ? Json<B> : never;

export type StaffMe = Res<'/v1/admin/me', 'get', 200>;
export type StaffTokens = Res<'/v1/admin/auth/email/verify', 'post', 200>;
export type Org = Res<'/v1/admin/orgs/{orgId}', 'get', 200>;
export type CreateOrg = Req<'/v1/admin/orgs', 'post'>;
export type AdminActivity = Res<'/v1/admin/orgs/{orgId}/activities/{activityId}', 'get', 200>;
export type CreateActivity = Req<'/v1/admin/orgs/{orgId}/activities', 'post'>;
export type Fan = Res<'/v1/admin/orgs/{orgId}/fans', 'get', 200>[number];
export type CreateStaff = Req<'/v1/admin/staff', 'post'>;
export type Rule = Res<'/v1/admin/orgs/{orgId}/rules', 'get', 200>[number];
export type RuleRequest = Req<'/v1/admin/orgs/{orgId}/rules', 'post'>;
export type Tier = Res<'/v1/admin/orgs/{orgId}/tiers', 'get', 200>[number];
export type TierRequest = Req<'/v1/admin/orgs/{orgId}/tiers', 'put'>[number];
export type Webhook = Res<'/v1/admin/orgs/{orgId}/webhooks', 'get', 200>[number];
export type WebhookRequest = Req<'/v1/admin/orgs/{orgId}/webhooks', 'post'>;
export type Media = Res<'/v1/admin/orgs/{orgId}/media', 'post', 201>;
export type WebhookDelivery = Res<'/v1/admin/orgs/{orgId}/webhooks/{webhookId}/deliveries', 'get', 200>[number];

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: unknown) {
    super(ApiError.describe(status, body));
  }
  private static describe(status: number, body: unknown): string {
    if (body && typeof body === 'object') {
      const b = body as { detail?: string; title?: string; errors?: Record<string, string[]> };
      if (b.errors) return Object.entries(b.errors).map(([k, v]) => `${k}: ${v.join(' ')}`).join(' · ');
      return b.detail ?? b.title ?? `Error ${status}`;
    }
    return `Error ${status}`;
  }
}

const SESSION_KEY = 'kk-platform:session';
interface Session { accessToken: string; expiresAt: number; refreshToken: string; staff: StaffMe }

let session: Session | null = load();
const listeners = new Set<() => void>();

function load(): Session | null {
  try { const raw = localStorage.getItem(SESSION_KEY); return raw ? (JSON.parse(raw) as Session) : null; } catch { return null; }
}
function save(next: Session | null): void {
  session = next;
  try { next ? localStorage.setItem(SESSION_KEY, JSON.stringify(next)) : localStorage.removeItem(SESSION_KEY); } catch { /* memory only */ }
  for (const l of [...listeners]) l();
}
function fromTokens(t: StaffTokens): Session {
  return { accessToken: t.accessToken, expiresAt: Date.now() + t.expiresIn * 1000, refreshToken: t.refreshToken, staff: t.staff };
}

export const auth = {
  get session() { return session; },
  subscribe(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  async start(email: string) { return call<Res<'/v1/admin/auth/email/start', 'post', 202>>('POST', '/v1/admin/auth/email/start', { email }, false); },
  async verify(challengeId: string, code: string) {
    const t = await call<StaffTokens>('POST', '/v1/admin/auth/email/verify', { challengeId, code }, false);
    save(fromTokens(t));
    return t.staff;
  },
  async signOut() {
    const s = session;
    save(null);
    if (s) await call('POST', '/v1/admin/auth/signout', undefined, true, s.accessToken).catch(() => undefined);
  },
};

let refreshing: Promise<string | null> | null = null;
async function token(): Promise<string | null> {
  if (!session) return null;
  if (session.expiresAt - Date.now() > 60_000) return session.accessToken;
  refreshing ??= (async () => {
    try {
      const t = await call<StaffTokens>('POST', '/v1/admin/auth/refresh', { refreshToken: session!.refreshToken }, false);
      save(fromTokens(t));
      return t.accessToken;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) save(null);
      return null;
    } finally { refreshing = null; }
  })();
  return refreshing;
}

async function call<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown, authed = true, explicitToken?: string): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (authed) {
    const t = explicitToken ?? (await token());
    if (!t) throw new ApiError(401, { detail: 'Signed out' });
    headers.Authorization = `Bearer ${t}`;
  }
  const res = await fetch(API_URL + path, { method, headers, body: body === undefined ? null : JSON.stringify(body) });
  const parsed: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && authed) save(null);
    throw new ApiError(res.status, parsed);
  }
  return parsed as T;
}

/** Uploads a picture to the content CDN; the URL goes into an activity's definition. */
async function upload(orgId: string, file: File): Promise<Media> {
  const t = await token();
  if (!t) throw new ApiError(401, { detail: 'Signed out' });
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(`${API_URL}/v1/admin/orgs/${orgId}/media`, { method: 'POST', headers: { Accept: 'application/json', Authorization: `Bearer ${t}` }, body: form });
  const parsed: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, parsed);
  return parsed as Media;
}

export const api = {
  me: () => call<StaffMe>('GET', '/v1/admin/me'),
  media: { upload },
  orgs: {
    list: () => call<Org[]>('GET', '/v1/admin/orgs'),
    get: (id: string) => call<Org>('GET', `/v1/admin/orgs/${id}`),
    create: (body: CreateOrg) => call<Org>('POST', '/v1/admin/orgs', body),
  },
  activities: {
    list: (orgId: string) => call<AdminActivity[]>('GET', `/v1/admin/orgs/${orgId}/activities`),
    get: (orgId: string, id: string) => call<AdminActivity>('GET', `/v1/admin/orgs/${orgId}/activities/${id}`),
    create: (orgId: string, body: CreateActivity) => call<AdminActivity>('POST', `/v1/admin/orgs/${orgId}/activities`, body),
    addVersion: (orgId: string, id: string, definition: unknown) => call<AdminActivity>('POST', `/v1/admin/orgs/${orgId}/activities/${id}/versions`, { definition }),
    publish: (orgId: string, id: string, versionId?: string) => call<AdminActivity>('POST', `/v1/admin/orgs/${orgId}/activities/${id}/publish`, { versionId: versionId ?? null }),
    close: (orgId: string, id: string) => call<AdminActivity>('POST', `/v1/admin/orgs/${orgId}/activities/${id}/close`, {}),
  },
  fans: { list: (orgId: string) => call<Fan[]>('GET', `/v1/admin/orgs/${orgId}/fans`) },
  rules: {
    list: (orgId: string) => call<Rule[]>('GET', `/v1/admin/orgs/${orgId}/rules`),
    create: (orgId: string, body: RuleRequest) => call<Rule>('POST', `/v1/admin/orgs/${orgId}/rules`, body),
    update: (orgId: string, id: string, body: RuleRequest) => call<Rule>('PUT', `/v1/admin/orgs/${orgId}/rules/${id}`, body),
    remove: (orgId: string, id: string) => call<null>('DELETE', `/v1/admin/orgs/${orgId}/rules/${id}`),
  },
  tiers: {
    list: (orgId: string) => call<Tier[]>('GET', `/v1/admin/orgs/${orgId}/tiers`),
    replace: (orgId: string, body: TierRequest[]) => call<Tier[]>('PUT', `/v1/admin/orgs/${orgId}/tiers`, body),
  },
  webhooks: {
    list: (orgId: string) => call<Webhook[]>('GET', `/v1/admin/orgs/${orgId}/webhooks`),
    create: (orgId: string, body: WebhookRequest) => call<Webhook>('POST', `/v1/admin/orgs/${orgId}/webhooks`, body),
    update: (orgId: string, id: string, body: WebhookRequest) => call<Webhook>('PUT', `/v1/admin/orgs/${orgId}/webhooks/${id}`, body),
    remove: (orgId: string, id: string) => call<null>('DELETE', `/v1/admin/orgs/${orgId}/webhooks/${id}`),
    deliveries: (orgId: string, id: string) => call<WebhookDelivery[]>('GET', `/v1/admin/orgs/${orgId}/webhooks/${id}/deliveries`),
  },
  staff: {
    list: () => call<StaffMe[]>('GET', '/v1/admin/staff'),
    create: (body: CreateStaff) => call<StaffMe>('POST', '/v1/admin/staff', body),
  },
};
